import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import type { PatientSearchResult } from '@wodi/shared';
import { Button, LoadingBlock, Notice, PageHeader, Section, TextArea, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { requestPatientMerge } from '../api/casualty.ts';
import { ApiError } from '../api/client.ts';
import { patientDetailQuery } from '../api/patients.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { PatientResultRow } from '../patients/patient-result-row.tsx';
import { PatientSearchPicker } from '../patients/patient-search-palette.tsx';
import { REASON_MIN_LENGTH } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './casualty.module.css';
import { errorText } from './register-unknown-dialog.tsx';

// Identify an unknown patient (docs/plans/phase2-casualty.md §5): choose the identified
// patient's record and say how you know; someone else approves the merge. The search term
// stays in this page's state and the request body only.

const route = getRouteApi('/_app/patients/$patientId/identify');

export function IdentifyPage() {
  const { patientId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const patient = useQuery(patientDetailQuery(patientId));
  const online = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [text, setText] = useState('');
  const [target, setTarget] = useState<PatientSearchResult | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const mayAsk = session !== null && can(session, 'patients.merge.request');
  const shortReason = reason.trim().length < REASON_MIN_LENGTH;

  const ask = useMutation({
    mutationFn: () =>
      requestPatientMerge(patientId, {
        clientRequestId,
        targetPatientId: target?.id ?? '',
        reason: reason.trim(),
      }),
    onSuccess: async (request) => {
      await queryClient.invalidateQueries({ queryKey: ['casualty'] });
      await queryClient.invalidateQueries({ queryKey: ['patient-merges'] });
      toasts.show({
        title: 'Merge requested',
        description: 'Someone with approval rights decides it.',
      });
      await navigate({ to: '/patient-merges/$requestId', params: { requestId: request.id } });
    },
  });
  const submit = () => {
    setTried(true);
    if (online && target !== null && !shortReason && !ask.isPending) ask.mutate();
  };
  useShortcut('mod+enter', submit, {
    allowInFields: true,
    enabled: mayAsk,
    description: 'Ask to merge',
  });

  if (patient.isPending) return <LoadingBlock label="Loading the patient" />;
  if (patient.isError) {
    return (
      <Notice
        tone="error"
        action={<Button onPress={() => void patient.refetch()}>Try again</Button>}
      >
        {errorText(patient.error, "The patient couldn't be loaded. Try again.")}
      </Notice>
    );
  }
  const open =
    ask.error instanceof ApiError && ask.error.code === 'merge_request_open'
      ? ask.error.context?.['requestId']
      : undefined;
  const p = patient.data;
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={patientId} />
      <PageHeader
        title="Identify patient"
        description="Choose the record of the person this is. Everything recorded so far will be shown under that record once someone else approves."
      />
      {!mayAsk ? (
        <Notice tone="info">
          You don't have permission to merge records. Ask someone at reception or a nurse.
        </Notice>
      ) : p.identity.state !== 'unknown' || p.status !== 'active' ? (
        <Notice tone="info">
          {p.mergedInto === null
            ? 'This patient is already identified.'
            : `This record was merged into ${p.mergedInto.displayName} (${p.mergedInto.patientNumber}).`}
        </Notice>
      ) : (
        <Section title="Who is this?">
          <form
            className={styles.form}
            aria-label="Identify patient"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {target === null ? (
              <>
                <PatientSearchPicker
                  inputValue={text}
                  onInputChange={setText}
                  onSelect={setTarget}
                  emptyAction={() => (
                    <span className={styles.meta}>
                      Not found? Register the person first, then come back to merge.
                    </span>
                  )}
                />
                {tried ? (
                  <Notice tone="error">Choose the identified patient&apos;s record.</Notice>
                ) : null}
              </>
            ) : (
              <div className={styles.actions}>
                <PatientResultRow result={target} />
                <Button
                  variant="quiet"
                  onPress={() => {
                    setTarget(null);
                    setText('');
                  }}
                >
                  Choose someone else
                </Button>
              </div>
            )}
            <TextArea
              label="How was the patient identified?"
              description="For example: identified by their sister, national ID seen. The approver reads this."
              value={reason}
              onChange={setReason}
              rows={3}
              {...(tried && shortReason
                ? { errorMessage: `Write at least ${String(REASON_MIN_LENGTH)} characters.` }
                : {})}
            />
            {!online ? (
              <Notice tone="info">
                No connection to Wodi. Asking to merge needs the connection; nothing is saved on
                this device.
              </Notice>
            ) : null}
            {ask.error === null ? null : (
              <Notice
                tone="error"
                {...(typeof open === 'string'
                  ? {
                      action: (
                        <Link to="/patient-merges/$requestId" params={{ requestId: open }}>
                          Open that request
                        </Link>
                      ),
                    }
                  : {})}
              >
                {errorText(ask.error, 'The merge could not be requested. Try again.')}
              </Notice>
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                isDisabled={!online}
                isPending={ask.isPending}
                pendingLabel="Asking…"
              >
                Ask to merge
              </Button>
              <ShortcutHint combo="mod+enter" action="ask to merge" />
            </div>
          </form>
        </Section>
      )}
    </div>
  );
}
